import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import Breadcrumb from "@/components/feature/Breadcrumb";
import VideoCard from "@/components/feature/VideoCard";
import Player from "./components/Player";
import ShareModal from "./components/ShareModal";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
  formatDate,
  formatViews,
  getCategoryById,
  getRelatedVideos,
  getVideoById,
} from "@/lib/videos";

export default function DetailPage() {
  const { videoId } = useParams();
  const video = getVideoById(videoId);
  const category = video ? getCategoryById(video.categoryId) : undefined;
  const [shareOpen, setShareOpen] = useState(false);
  const [enlargeOpen, setEnlargeOpen] = useState(false);

  usePageMeta(
    video ? `${video.title} - 赛事黑榜视频详情 - 51视频每日大赛` : "视频不存在 - 51视频每日大赛今日黑榜合集",
    video
      ? `${video.description} 收录于${category ? category.name : "赛事黑榜"}，上传于 ${formatDate(video.uploadedAt)}。`
      : "该视频可能已下线，请返回首页浏览最新的每日赛事黑榜视频合集。",
  );

  if (!video) {
    return (
      <PageShell>
        <div className="container-page py-20 text-center">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
            <i className="ri-file-unknow-line" />
          </span>
          <h1 className="mt-5 text-2xl font-bold text-foreground-950">没有找到这条视频</h1>
          <p className="mx-auto mt-3 max-w-md text-sm text-foreground-600">
            视频编号不存在或内容已调整。你可以返回首页查看今日更新，或按分类继续浏览黑榜档案。
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link to="/" className="btn-primary">返回首页</Link>
            <Link to="/category/football" className="btn-ghost">进入分类</Link>
          </div>
        </div>
      </PageShell>
    );
  }

  const related = getRelatedVideos(video, 5);
  const infoRows = [
    { label: "赛事类型", value: category ? category.name : "赛事" },
    { label: "比赛等级", value: video.level },
    { label: "所在地区", value: video.region },
    { label: "收录期数", value: video.episode },
    { label: "视频时长", value: video.duration },
    { label: "上传日期", value: formatDate(video.uploadedAt) },
    { label: "观看量", value: `${formatViews(video.views)} 次` },
    { label: "上传者", value: video.uploader },
  ];

  return (
    <PageShell>
      <div className="border-b border-background-200 bg-background-100">
        <div className="container-page flex flex-wrap items-center justify-between gap-3 py-4">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: category ? category.name : "赛事分类", to: `/category/${video.categoryId}` },
              { label: video.episode },
            ]}
          />
          <Link
            to={`/category/${video.categoryId}`}
            className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-foreground-600 transition-colors hover:text-primary-700"
          >
            <i className="ri-arrow-left-line" />
            返回{category ? category.name : "分类"}
          </Link>
        </div>
      </div>

      <div className="container-page py-7 md:py-9">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-10">
          <article>
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to={`/category/${video.categoryId}`}
                className="chip bg-primary-50 text-primary-700 transition-colors hover:bg-primary-100"
              >
                <i className={`${category ? category.icon : "ri-trophy-line"} text-sm`} />
                {category ? category.name : "赛事"}
              </Link>
              <span className="chip bg-secondary-100 text-secondary-800">{video.episode}</span>
              <span className="chip bg-accent-100 text-accent-900">{video.level}</span>
            </div>

            <h1 className="mt-3 text-[24px] font-bold leading-snug text-foreground-950 md:text-[32px] md:leading-[1.3]">
              {video.title}
            </h1>

            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-3-line text-sm" />
                {video.uploader}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line text-sm" />
                {formatDate(video.uploadedAt)}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line text-sm" />
                {formatViews(video.views)} 次观看
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line text-sm" />
                {video.duration}
              </span>
            </div>

            <div className="mt-5">
              <Player
                cover={video.cover}
                title={video.title}
                duration={video.duration}
                onEnlarge={() => setEnlargeOpen(true)}
              />
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <button type="button" onClick={() => setShareOpen(true)} className="btn-primary h-10 px-4 text-sm">
                <i className="ri-share-forward-line text-base" />
                分享视频
              </button>
              <button
                type="button"
                onClick={() => setEnlargeOpen(true)}
                className="btn-ghost h-10 px-4 text-sm"
              >
                <i className="ri-expand-diagonal-line text-base" />
                封面大图
              </button>
              <Link to={`/category/${video.categoryId}`} className="btn-ghost h-10 px-4 text-sm">
                <i className="ri-layout-grid-line text-base" />
                同分类视频
              </Link>
            </div>

            <section className="mt-9" aria-labelledby="video-intro-title">
              <div className="flex items-center gap-3">
                <h2 id="video-intro-title" className="text-lg font-bold text-foreground-950">视频简介</h2>
                <span className="h-px flex-1 bg-background-200" />
              </div>
              <p className="mt-3 text-[14px] leading-[1.9] text-foreground-700">{video.description}</p>
            </section>

            <section className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5" aria-labelledby="video-notes-title">
              <h2 id="video-notes-title" className="flex items-center gap-2 text-lg font-bold text-foreground-950">
                <i className="ri-draft-line text-lg text-primary-600" />
                赛事笔记
              </h2>
              <p className="mt-3 text-[14px] leading-[1.9] text-foreground-700">{video.notes}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {video.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="chip bg-background-50 text-foreground-600 transition-colors hover:bg-secondary-100 hover:text-secondary-900"
                  >
                    <i className="ri-price-tag-3-line text-xs" />
                    {tag}
                  </Link>
                ))}
              </div>
            </section>

            <div className="mt-6 rounded-md border border-accent-200 bg-accent-50 px-4 py-3">
              <p className="text-xs leading-relaxed text-accent-900">
                <i className="ri-alert-line mr-1.5 align-[-2px]" />
                本页播放区域为前端模拟演示，用于展示播放交互，不包含真实赛事视频，也不提供任何直播或下载服务。
              </p>
            </div>

            <section className="mt-10" aria-labelledby="related-title">
              <div className="flex items-center justify-between gap-3">
                <h2 id="related-title" className="text-lg font-bold text-foreground-950">
                  相关推荐
                </h2>
                <Link
                  to={`/category/${video.categoryId}`}
                  className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-foreground-600 transition-colors hover:text-primary-700"
                >
                  查看全部
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-x-5 gap-y-6 sm:grid-cols-2">
                {related.slice(0, 2).map((item) => (
                  <VideoCard key={item.id} video={item} compact />
                ))}
              </div>
            </section>
          </article>

          <aside className="space-y-5">
            <div className="panel overflow-hidden">
              <div className="flex items-center justify-between border-b border-background-200 px-4 py-3">
                <h2 className="text-sm font-bold text-foreground-950">视频资料</h2>
                <span className="font-label text-[10px] tracking-[0.2em] text-foreground-400">INFO</span>
              </div>
              <dl className="divide-y divide-background-200">
                {infoRows.map((row) => (
                  <div key={row.label} className="flex items-center justify-between gap-3 px-4 py-2.5 text-[13px]">
                    <dt className="text-foreground-500">{row.label}</dt>
                    <dd className="text-right font-medium text-foreground-900">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="panel p-4">
              <h2 className="flex items-center gap-2 text-sm font-bold text-foreground-950">
                <i className="ri-fire-line text-base text-accent-600" />
                {category ? category.name : "分类"}热门
              </h2>
              <div className="mt-3 space-y-3">
                {related.map((item, index) => (
                  <Link
                    key={item.id}
                    to={`/detail/${item.id}`}
                    className="group flex items-center gap-3"
                  >
                    <span className="w-4 shrink-0 font-label text-xs text-foreground-400">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <span className="h-11 w-16 shrink-0 overflow-hidden rounded bg-secondary-200">
                      <img
                        src={item.cover}
                        alt={`${item.title} 缩略图`}
                        title={item.title}
                        className="h-full w-full object-cover object-top"
                      />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-xs font-medium text-foreground-800 transition-colors group-hover:text-primary-700">
                        {item.title}
                      </span>
                      <span className="mt-0.5 block font-label text-[10px] tracking-wide text-foreground-400">
                        {formatViews(item.views)} 次观看
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-primary-100 bg-primary-50 p-4">
              <h2 className="text-sm font-bold text-primary-800">快捷跳转</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                <Link to="/" className="rounded-full bg-background-50 px-3 py-1.5 text-xs text-primary-800 transition-colors hover:bg-primary-100">
                  首页
                </Link>
                <Link to="/search" className="rounded-full bg-background-50 px-3 py-1.5 text-xs text-primary-800 transition-colors hover:bg-primary-100">
                  搜索
                </Link>
                <Link to="/about" className="rounded-full bg-background-50 px-3 py-1.5 text-xs text-primary-800 transition-colors hover:bg-primary-100">
                  关于
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <ShareModal open={shareOpen} title={video.title} onClose={() => setShareOpen(false)} />

      {enlargeOpen && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-foreground-950/85 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="封面大图"
          onClick={() => setEnlargeOpen(false)}
        >
          <div className="anim-fade-in relative w-full max-w-4xl" onClick={(event) => event.stopPropagation()}>
            <img
              src={video.cover}
              alt={`${video.title} 封面大图`}
              title={`${video.title} 封面大图`}
              className="max-h-[78vh] w-full rounded-lg object-contain"
            />
            <div className="mt-3 flex items-center justify-between gap-4">
              <p className="text-sm text-background-100">{video.title}</p>
              <button
                type="button"
                onClick={() => setEnlargeOpen(false)}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-background-50/30 text-background-50 transition-colors hover:bg-background-50/10"
                aria-label="关闭封面大图"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}